<template>
  <div>
    <component
      :is="componentType"
      v-bind="currentProperties()"
      v-model="newValue"
    >
      <template #message="{ message, key }">
        <div :key="key" v-html="message"></div>
      </template>
      <v-radio
        v-for="option in field.options"
        v-if="componentType === 'v-radio-group'"
        :key="option.value"
        :label="option.text"
        :value="option.value"
      />
    </component>
  </div>
</template>

<script setup>
import { useStore } from 'vue2-helpers/vuex';
import { ref, watch } from 'vue';

// eslint-disable-next-line no-undef
const emit = defineEmits(['input']);

// eslint-disable-next-line no-undef
const props = defineProps({
  field: { type: Object, default: () => { } },
  value: { type: [Array, Number, String, Boolean], default: null }
});

const store = useStore();

const newValue = ref(props.value);
const componentType = ref('v-text-field');

watch(() => props.value, value => {
  newValue.value = value;
});

watch(newValue, value => {
  const val = props.field.trim && typeof (value) === 'string' ? value.trim() : value;

  if (props.field.viewType === 'Number') {
    emit('input', !Number.isNaN(parseInt(val)) ? Number(val) : null);
  } else if (props.field.viewType === 'Select') {
    emit('input', val || null);
  } else if (props.field.viewType === 'Switch') {
    emit('input', !!val);
  } else {
    emit('input', val === '' ? null : val);
  }
});

const currentProperties = () => {
  const properties = {
    rules: props.field.rules,
    hint: props.field.helpText,
    label: props.field.label,
    readonly: props.field.readonly,
    persistentHint: props.field.alwaysVisibleHelpText,
    color: 'info'
  };

  if (['Text', 'Number', 'Select', 'Textarea'].includes(props.field.viewType)) {
    properties['background-color'] = 'secondary';
  }

  switch (props.field.viewType) {
    case 'Text':
      properties.type = 'text';
      break;
    case 'Number':
      properties.type = 'number';
      if (props.field.minNumber !== null) properties.min = props.field.minNumber;
      if (props.field.maxNumber !== null) properties.max = props.field.maxNumber;
      break;
    case 'Select':
      componentType.value = 'v-select';

      properties['menu-props'] = { light: true };

      if (props.field.optionsByReference) {
        const options = store.getters.getReference(props.field.optionsByReference);
        properties.items = options ? Object.entries(options).reduce((acc, [key, value]) => {
          acc.push({
            value: key,
            text: typeof value === 'object' ? value[store.getters.locale] : value
          });
          return acc;
        }, []) : [];
      } else {
        properties.items = props.field.options;
      }
      if (props.field.maxCount) {
        properties.counter = props.field.maxCount;
        properties['item-disabled'] = item => {
          if (!newValue.value || (newValue.value.includes(item.value) || newValue.value.length < props.field.maxCount)) {
            return false;
          }
          return true;
        };
      }
      properties.multiple = props.field.multivalued;
      properties['item-color'] = 'info';
      if (!properties.readonly) properties.clearable = true;
      break;
    case 'Slider':
      componentType.value = 'v-slider';
      break;
    case 'Textarea':
      componentType.value = 'v-textarea';
      properties['background-color'] = 'primary';
      properties.filled = true;
      break;
    case 'Checkbox':
      componentType.value = 'v-checkbox';
      properties['false-value'] = props.field.falseValue;
      properties['true-value'] = props.field.trueValue;
      break;
    case 'Switch':
      componentType.value = 'v-switch';
      properties['persistent-hint'] = true;
      properties.class = 'switcher';
      break;
    case 'Radio button':
      componentType.value = 'v-radio-group';
      break;
    default:
  }
  return properties;
};

</script>

<style lang="scss" scoped>

</style>
